import { supabase } from '../lib/supabase'
import { whatsappTicketsService } from './whatsappTickets'
import { zendeskTicketsService } from './zendeskTickets'
import type {
  TicketAnalysisListParams,
  TicketAnalysisListResult,
  TicketAnalysisMode,
  TicketAnalysisRow,
  WppDialogResult,
  WppOrigin,
  WppTicketHeader,
  ZendeskDialogResult,
  ZendeskTicketHeader,
} from '../types/ticketAnalysis'

const BATCH_SIZE = 1000

function parseJson(data: unknown): Record<string, unknown> | null {
  if (!data) return null
  try {
    if (typeof data === 'string') return JSON.parse(data) as Record<string, unknown>
    return data as Record<string, unknown>
  } catch {
    return null
  }
}

function analysisOutput(row: TicketAnalysisRow): Record<string, unknown> | null {
  const parsed = parseJson(row.json)
  if (!parsed) return null
  const output = parsed.output
  if (output && typeof output === 'object') return output as Record<string, unknown>
  return parsed
}

function protocolFromRow(row: TicketAnalysisRow): string {
  const output = analysisOutput(row)
  const protocolo = output?.protocolo
  return String(protocolo ?? row.ticket_id ?? '')
}

function matchesChannel(row: TicketAnalysisRow, needle: string): boolean {
  return (row.channel ?? '').toString().toLowerCase().includes(needle)
}

function rowOrigin(row: TicketAnalysisRow): string {
  return (row.origin ?? '').toString().trim()
}

function matchesWhatsappOrigin(row: TicketAnalysisRow, origin: WppOrigin): boolean {
  const value = rowOrigin(row)
  if (value === origin) return true
  // Análises antigas do bot principal podem estar sem origin preenchido.
  return origin === 'bot-principal' && value === ''
}

function isWhatsappOriginValue(value: string): boolean {
  return value === 'bot-principal' || value === 'pasi-farma'
}

async function fetchAllAnalyses(
  dataInicio: string,
  dataFim: string,
  mode: TicketAnalysisMode,
  origin?: WppOrigin,
): Promise<TicketAnalysisRow[]> {
  const startIso = new Date(`${dataInicio}T00:00:00`).toISOString()
  const endIso = new Date(`${dataFim}T23:59:59`).toISOString()
  const allData: TicketAnalysisRow[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    let query = supabase
      .from('ticket_analysis')
      .select('*')
      .eq('channel', mode)
      .gte('analysis_date', startIso)
      .lte('analysis_date', endIso)
      .order('analysis_date', { ascending: false })

    if (mode === 'whatsapp') {
      if (origin === 'bot-principal') {
        query = query.or('origin.eq.bot-principal,origin.is.null')
      } else if (origin) {
        query = query.eq('origin', origin)
      }
    }

    const { data, error } = await query.range(from, from + BATCH_SIZE - 1)

    if (error) throw error

    if (data && data.length > 0) {
      allData.push(...(data as TicketAnalysisRow[]))
      if (data.length < BATCH_SIZE) hasMore = false
      else from += BATCH_SIZE
    } else {
      hasMore = false
    }
  }

  return allData
}

async function fetchZdHeaders(ticketIds: string[]): Promise<ZendeskTicketHeader[]> {
  return zendeskTicketsService.listByTicketIds(ticketIds)
}

export const ticketAnalysisService = {
  async listByDateRange(params: TicketAnalysisListParams): Promise<TicketAnalysisListResult> {
    const { mode, origin, dataInicio, dataFim } = params
    if (mode === 'whatsapp' && !origin) {
      return { rows: [], wppHeadersByProtocol: {}, zdHeadersByTicketId: {} }
    }

    const allRows = await fetchAllAnalyses(dataInicio, dataFim, mode, origin)
    const channelNeedle = mode === 'whatsapp' ? 'whatsapp' : 'zendesk'
    const channelRows = allRows.filter((row) => {
      if (!matchesChannel(row, channelNeedle)) return false
      if (mode === 'whatsapp' && origin) return matchesWhatsappOrigin(row, origin)
      // Zendesk não deve herdar análises gravadas para os bots WhatsApp.
      if (mode === 'zendesk' && isWhatsappOriginValue(rowOrigin(row))) return false
      return true
    })

    if (mode === 'whatsapp') {
      const wppOrigin = origin as WppOrigin
      const protocols = Array.from(
        new Set(channelRows.map(protocolFromRow).filter(Boolean))
      )
      const headers = protocols.length > 0 ? await whatsappTicketsService.listByProtocols(protocols, wppOrigin) : []
      const wppHeadersByProtocol: Record<string, WppTicketHeader> = {}
      headers.forEach((header) => {
        if (header?.protocol && header.origin === wppOrigin) {
          wppHeadersByProtocol[header.protocol] = header
        }
      })

      const rows = channelRows.filter((row) => {
        const protocol = protocolFromRow(row)
        return Boolean(protocol && wppHeadersByProtocol[protocol])
      })

      return { rows, wppHeadersByProtocol, zdHeadersByTicketId: {} }
    }

    const ticketIds = Array.from(
      new Set(channelRows.map((row) => row.ticket_id).filter(Boolean))
    )
    const zdHeaders = ticketIds.length > 0 ? await fetchZdHeaders(ticketIds) : []
    const zdHeadersByTicketId: Record<string, ZendeskTicketHeader> = {}
    zdHeaders.forEach((header) => {
      if (header?.ticket_id) zdHeadersByTicketId[header.ticket_id] = header
    })

    return { rows: channelRows, wppHeadersByProtocol: {}, zdHeadersByTicketId }
  },

  async getWppDialog(
    protocol: string,
    origin: WppOrigin,
    ticketListId?: number | null,
  ): Promise<WppDialogResult> {
    const header =
      ticketListId != null
        ? await whatsappTicketsService.getById(ticketListId, origin)
        : await whatsappTicketsService.getByProtocolOrigin(protocol, origin)

    if (!header) return { header: null, messages: [] }

    const messages = await whatsappTicketsService.getMessagesByTicketListId(header.id)
    return { header, messages }
  },

  async getZendeskDialog(ticketId: string): Promise<ZendeskDialogResult> {
    const [header, messages] = await Promise.all([
      zendeskTicketsService.getByTicketId(ticketId),
      zendeskTicketsService.getMessagesByTicketId(ticketId),
    ])

    const idSet = new Set<number>()
    messages.forEach((msg) => {
      const idNum = msg.author_id == null ? null : Number(msg.author_id)
      if (idNum != null && Number.isFinite(idNum)) idSet.add(idNum)
    })

    const usersById = await zendeskTicketsService.getUsersByIds(Array.from(idSet))

    return { header, messages, usersById }
  },
}
